import { MeasurementUnitEnum } from 'domains/measurementDomain';
import {
  UploadFile,
  UploadFileStatus,
  UploadProps,
} from 'antd/lib/upload/interface';
import { ReactElement } from 'react';
import {
  JobFullfillmentShippingInfoInternalDistribution,
  ShippingAccountTypeEnum,
} from './jobconfigDomain';
import {
  BillingInfo,
  BillingRequestor,
  CostDistributionPayload,
  ShipmentTypeEnum,
} from './orderDomain';
import { CarrierTypeEnum } from './shippingDomain';
import { InkEnum, OrientationEnum } from './skuDomain';
import { JobItem, JobParticipantEnum } from './workspaceDomain';

export type SvcOptions = {
  label: string;
  value: string;
};

export type JobSummarySvcItems = {
  svcName: string;
  svcCharge: number;
  svcDescription: string;
  svcType: string;
  options: SvcOptions[];
};

export type JobSummaryShippingItems = {
  serialNumber: number;
  carrierType: string;
  shipmentType: ShipmentTypeEnum;
  carrierService: string;
  estimatedShipTime: string;
  estimatedDeliveryTime: string;
  // free shipping
  shippingFree: Boolean;

  shippingCost: number;
  shippingMarkup: number;
  supplierFreightCharge: number;

  revshopShippingMarkup: number;
  revxFreightCharge: number;

  outsourceShippingMarkup: number;
  outsourcerFreightCharge: number;

  freightCharge: number;
  parcelCost: number;
  parcelCharge: number;
  parcelMarkup: number;
  shippingTotal: number;
};

export type JobSummaryParts = {
  skuId: number;
  partName: string;
  displayName: string;
  skuTypeName: string;

  pageCount: number;
  pageCountEditable: boolean;

  paperBasisWeight: string;
  paperFinish: string;
  paperCategory: string;
  paperColor: string;
  paperCompany: string;
  paperBrand: string;
  paperName: string;

  pressName: string;
  inkFront: InkEnum;
  inkBack: InkEnum;
  coatingFront: string;
  coatingBack: string;
  pmsBack: string[];
  pmsFront: string[];

  // paper + elementMarkup.
  paperCharge: number;
  // press + elementMarkup
  pressCharge: number;

  svcs: JobSummarySvcItems[];
};

export type JobSummaryParcel = {
  /** Always in inches; `dimensionsMetric` carries the millimetre form. Read-only. */
  dimensions: string[];
  /** The millimetre form, sent alongside so switching units needs no refetch. Read-only. */
  dimensionsMetric?: string[];
  charge: number;
};

export type Summary = {
  /** Metric twin of `finishedSizeIn`, sent alongside so a unit switch needs no refetch. */
  finishedSizeMetric?: string;
  /**
   * The ready-made size this job was configured with, e.g. `A4`.
   *
   * Shown instead of the dimensions, but only to a reader working in `finishedSizePresetUnit`.
   */
  finishedSizePreset?: string;
  finishedSizePresetUnit?: MeasurementUnitEnum;
  skuId: number;
  wrapperName: string;
  displayName: string;
  finishedSizeIn: string;
  orientation: OrientationEnum;

  parcel: JobSummaryParcel;

  parts: JobSummaryParts[];

  svcs: JobSummarySvcItems[];

  shippings: JobSummaryShippingItems[];

  globalMarkup: number;
  supplierPrintingCharge: number;
  supplierFreightCharge: number;
  supplierCharge: number;

  revshopMarkup: number;
  revshopShippingMarkup: number;
  shippingMarkup: number;
  outsourceShippingMarkup: number;
  revxPrintingCharge: number;
  revxFreightCharge: number;
  revxFreightCost: number;
  revxCharge: number;

  outsourceMarkup: number;
  outsourcePrintingCharge: number;
  outsourceFreightCharge: number;
  outsourceCharge: number;

  printingTotal: number;
  shippingTotal: number;
  separatelyFreightCost: number;
  total: number;
  tax: number;
  markupCharge: number;
  invoice: SummaryInvoice;

  digitalProofRequired: boolean;
  hardcopyProofRequired: boolean;
};

export type SummaryInvoice = {
  // supplierInvoiceFile?: SupplierInvoiceReferenceFiles;
  supplierInvoiceNumber?: number;
  supplierInvoiceNumberAttached: boolean;
  supplierInvoiceReferenceFiles?: SupplierInvoiceReferenceFiles[];
  invoiceAt?: string;
  markAsPaidTime?: string;
};

export type SupplierInvoiceSubmitParams = {
  jobId: number;
  invoice: SupplierInvoiceFile;
};

export type SupplierInvoiceFile = {
  invoiceNumber: number;
  file: SupplierInvoiceReferenceFiles;
};

export type SupplierInvoiceReferenceFiles = { name: string; path: string };

export type FileItem = {
  createAt: string;
  creatorId: number;
  fileName: string;
  fileUrl: string;
  id: number;
  jobId: number;
  jobProofId: number;
  skuId: number;
  updateAt: string;
  name: string;
  uid: string;
  path: string;
  status: UploadFileStatus;
  thumbUrl: string;
  url: string;
  urlPrefix: string;
  canDelete?: boolean;
  filesLen?: number;
  needHighResolution?: number;
};

export type FileData = {
  fileName: string;
  path: string;
}[];

export type UploadAction = {
  download: (option: UploadFile<FileItem>) => void;
  preview: (option: UploadFile<FileItem>) => void;
  remove: (option: UploadFile<FileItem>) => void;
};
export interface CommonUploadFileData extends UploadProps {
  displayNameForUrl?: boolean;
  defaultFileList: UploadFile<any>[];
  onUploadFileChange: (x: UploadFile<any>[]) => void;
  showUploadButton: boolean;
  itemData: JobItem | ProofItem;
  deleteFileHandler: (option: any) => Promise<string>;
  getAllFileHandler: (option: any) => Promise<FileItem[]>;
  saveFileHandler: (option: any) => Promise<UploadFile<FileItem>>;
  setIsUploading: (option: boolean) => void;
  uploadIconBeforeOldFiles?: boolean;
  customerItemRender?: (
    originNode: ReactElement,
    file: UploadFileOptions<any>,
    fileList: UploadFile<FileItem>[],
    actions: UploadAction
  ) => JSX.Element;
  getUploadFileList?: (option: any) => void;
  onFileListChange?: (fileList: Array<UploadFile<FileItem>>) => void;
}

export interface UploadFileOptions<T> extends UploadFile<T> {
  canDelete?: boolean;
  needHighResolution?: number;
}

export type DynamicComponent = { [k: string]: React.ElementType };

export enum ProofStatusEnum {
  DRAFT = 'DRAFT',
  WAITING_APPROVE = 'WAITING_APPROVE',
  APPROVE = 'APPROVE',
  DECLINED = 'DECLINED',
}

export enum ProofTypeEnum {
  HARD_COPY = 'HARD_COPY',
  DIGITAL = 'DIGITAL',
}

export type ProofTab = {
  [ProofTypeEnum.DIGITAL]: ProofItem[];
  [ProofTypeEnum.HARD_COPY]: ProofItem[];
};

export type ProofMsg = {
  createAt: string;
  creatorFirstName: string;
  creatorId: number;
  creatorLastName: string;
  creatorFullName: string;
  id: number;
  jobProofId: number;
  msg: string;
  proofVersion: number;
  updateAt: string;
};

export type ProofItem = {
  approverId: number;
  id: number;
  jobId: number;
  proofStatus: ProofStatusEnum;
  proofType: ProofTypeEnum;
  proofApproveAt: string;
  proofSendAt: string;
  proofVersion: number;
  requesterId: number;
  toAddressLine: string;
  toCity: string;
  toCountryCode: string;
  toName: string;
  toPostalCode: string;
  toStateProvinceCode: string;
  trackingCode: string;
  proofMsg: ProofMsg[];
  skipArtFile?: boolean;
};

// Supplier Shipping
/*export enum ShipmentTypeEnum {
  PACKAGE,
  FREIGHT,
}*/

export interface JobShippingItemSpec {
  id: number;
  serialNumber: number;
  shipmentType: ShipmentTypeEnum;
  itemQuantity: number;
  carrierType: CarrierTypeEnum;
  carrierService: string;
  toCompany: string;
  toName: string;
  toAddressLine: string;
  toCity: string;
  toStateProvinceCode: string;
  toPostalCode: string;
  toCountryCode: string;
  phoneNumber: string;
  estimatedShipTime: string;
  estimatedDeliveryTime?: string;
  originalFulfillment: JobShippingItemFulfillment;
  supplierFulfillment: JobShippingItemFulfillment;
}
export interface JobShippingSummaryItem {
  id: number;
  name: string;
  quantity: number;
  actualShipTime: string;
  actualDeliveryTime: string;
  estimatedShipTime: string;
  estimatedDeliveryTime?: string;
  shippingInfo: ShippingInfo;
  readyDate: string;
  shippingDetail: string;
}
export interface JobShippingItemFulfillment {
  id: number;
  name: string;
  quantity: number;
  carrierType: CarrierTypeEnum;
  carrierService: string;
  actualShipTime: string;
  actualDeliveryTime: string;
  estimatedShipTime: string;
  estimatedDeliveryTime?: string;
  shippingInfo: ShippingInfo;
  freightCharge: number;
  printingCharge: number;
  supplierFreightCharge: number;
  supplierPrintingCharge: number;
  supplierTotalCharge: number;
  taxCharge: number;
  totalCharge: number;
  readyDate: string;
  shippingDetail: string;
}
export interface ShippingInfo {
  trackingCode: string;
  fromName: string;
  fromAddressLine: string;
  fromCity: string;
  fromStateProvinceCode: string;
  fromPostalCode: string;
  fromCountryCode: string;
  toName: string;
  toAddressLine: string;
  toCity: string;
  toStateProvinceCode: string;
  toPostalCode: string;
  toCountryCode: string;
  carrierService: string;
  carrierType: CarrierTypeEnum;
  shipmentType: ShipmentTypeEnum;
  accountType: ShippingAccountTypeEnum;
  estimatedDeliveryTime: string;
  estimatedShipTime: string;
  businessDaysInTransit?: number;
  account: string;
  accountCompany: string;
  internalDistribution?: JobFullfillmentShippingInfoInternalDistribution;
  actualShipTime: string;
}

export type JobShippingItemFulfillmentUpdateRequest = {
  actualShipTime?: string;
  carrierService?: string;
  trackingCode?: string;
};

export type JobNotes = {};

export enum JobNotesCategoryEnum {
  APPROVAL = 'APPROVAL',
  CONFIGURATION = 'CONFIGURATION',
  PRICE = 'PRICE',
  PROOF = 'PROOF',
  JOB_FILES = 'JOB_FILES',
  SHIPPING = 'SHIPPING',
  OTHER = 'OTHER',
  ART_FILE_INSTRUCTIONS = 'ART_FILE_INSTRUCTIONS',
  SHIPPING_INSTRUCTIONS = 'SHIPPING_INSTRUCTIONS',
}

export type JobNotesItem = {
  jobId: number;
  category: JobNotesCategoryEnum;
  content: string;
  createAt: string;
  creatorFullName: string;
  creatorFirstName: string;
  creatorLastName: string;
  creatorId: number;
  id: number;
  updateAt: string;
};

export type JobPoItem = {
  jobId: number;
  sellPrice: number;
  supplierOrgId: number;
};

export type JobPoViewBillRequestor = {
  addressLine1: string;
  addressLine2: string;
  addressLine: string;
  attention: string;
  city: string;
  country: string;
  email: string;
  name: string;
  postalCode: string;
  resaleNumber: string;
  state: string;
  telephone: string;
};

export type JobPoView = {
  billTo: JobPoViewBillRequestor;

  jobSummary: Summary;

  requestor: JobPoViewBillRequestor;
};

export type OrgMembers = {
  memberId: number;
  memberName: string;
};

export type CompareWithOrgPartsAvlPresses = {
  id: number;
  name: string;
};

export type CompareWithOrgPartsSvcs = {
  charge: number;
  description: string;
  id: string;
};

export type CompareWithOrgParts = {
  avlPresses: CompareWithOrgPartsAvlPresses[];
  paperCategory: string;
  paperCharge: number;
  paperId: number;
  /** Always in pounds; `paperWeightMetric` carries the gsm form. Read-only. */
  paperWeight: string;
  /** The gsm form, sent alongside so switching units needs no refetch. Read-only. */
  paperWeightMetric?: string;
  pressCharge: number;
  pressId: number;
  pressName: string;
  svcs: CompareWithOrgPartsSvcs[];
};

export type CompareWithOrgShippings = {
  carrierService: string;
  carrierType: string;
  charge: number;
};

export type CompareWithOrg = {
  name: string;
  orgMarkup: number;
  orgMarkupPct: number;
  parts: CompareWithOrgParts[];
  poPrice: number;
  revshopMarkup: number;
  revshopMarkupPct: number;
  sellPrice: number;
  shippings: CompareWithOrgShippings[];
  supplierPrice: number;
  svcs: CompareWithOrgPartsSvcs[];
};

export interface JobShippingItemSummary {
  items: JobShippingItemFulfillment[];
  freightCharge: number;
  supplierAdjustCharge: number;
  supplierFreightCharge: number;
  supplierTotalCharge: number;
  totalCharge: number;
}

export type SimpleOrg = {
  id: number;
  name: string;
  company: string;
  logo: string;
};

export interface WorkspaceTabSummaryDTO {
  printing: Printing;
  shipping: Shipping;
  adjustment: Adjustment | null;
  revisions: Revision[];
  tax: number | null;
  markup: number | null;
  total: number;
  // History adjustments
  historyAdjustments: Adjustment[];
}

export interface Printing {
  /** Metric twin of `finishedSize`, sent alongside so switching units needs no refetch. */
  finishedSizeMetric?: string;
  /**
   * The ready-made size this job was configured with, e.g. `A4`.
   *
   * Shown instead of the dimensions, but only to a reader working in `finishedSizePresetUnit`.
   */
  finishedSizePreset?: string;
  finishedSizePresetUnit?: MeasurementUnitEnum;
  name: string;
  quantity: number;
  orientation: OrientationEnum;
  finishedSize: string;
  parts: PrintingPart[];
  services: string[];
  charge: number;
}

export interface PrintingPart {
  name: string;
  paper: string;
  color: string;
  press: string;
}

export interface Shipping {
  items: ShippingItem[];
  charge: number;
  readyDate: string;
  extraSupplierShippingCharge: number;
}

export interface ShippingItem {
  serialNumber: number;
  itemQuantity: number;
  expectedDeliveryDate: string;
  readyDate: string;
  arrivalDate: string;
  charge: number;
  billedSeparately: boolean;
  shippingInfo: ShippingInfo;
}

export interface Adjustment {
  isAdjusted: boolean;
  adjustmentId: number;
  adjustedAt: string;
  adjustmentCharge: number;
}

export interface Revision {
  revisionId: number;
  revisionJobId: string;
  revisionAt: string;
}

export interface WorkspaceTabShippingBillingDTO {
  participants: JobParticipantEnum[];
  producerBilling: ProducerBilling;
  customerBilling: CustomerBilling;
  producerShipping: ProducerShipping;
  customerShipping: CustomerShipping;
  invoice: SummaryInvoice;
  costDistribution?: CostDistributionPayload;
}

interface ProducerBilling {
  billTo: BillingItem;
  requestor: BillingItem;
}

export interface BillingItem {
  name: string;
  addressLine1: string;
  addressLine2: string;
  state: string;
  city: string;
  country: string;
  postalCode: string;
  attention: string;
  email: string;
  telephone: string;
  resaleNumber: string;
}

interface CustomerBilling {
  billingInfo: BillingInfo;
  billingRequestor: BillingRequestor;
}

interface ProducerShipping {
  readyDate: string;
  items: ShippingBillingProducerItem[];
}

export interface ShippingBillingProducerItem {
  specId: number;
  fulfillmentId: number;
  serialNumber: number;
  itemQuantity: number;
  expectedDeliveryDate: string;
  price: number;
  shipped: boolean;
  shippingInfo: ShippingInfo;
  shippingDetail: string;
  shipDate: string;
}

export interface ShippingBillingCustomerItem {
  specId: number;
  fulfillmentId: number;
  serialNumber: number;
  itemQuantity: number;
  expectedDeliveryDate: string;
  price: number;
  shipped: boolean;
  shippingInfo: ShippingInfo;
  shippingDetail: string;
  shipDate: string;
  billedSeparately: boolean;
}

interface CustomerShipping {
  readyDate: string;
  items: ShippingBillingCustomerItem[];
}
